import { useState, useEffect, useRef } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  limit,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

const useProfile = (currentUser) => {
  const hasFetched = useRef(false);
  const [profile, setProfile] = useState(null);
  const [referralLinks, setReferralLinks] = useState([]);
  const [userActivities, setUserActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentUser && !hasFetched.current) {
      hasFetched.current = true;
      fetchReferralLinks(currentUser.uid);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(
      doc(db, "users", currentUser.uid),
      (docSnap) => {
        if (docSnap.exists()) setProfile(docSnap.data());
      }
    );
    return () => unsubscribe();
  }, [currentUser]);

  const fetchReferralLinks = async (memberId) => {
    try {
      setLoading(true);
      const referralLinksRef = collection(db, "referralLinks");
      const q = query(referralLinksRef, where("memberId", "==", memberId));
      const snapshot = await getDocs(q);
      const links = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setReferralLinks(links);
    } catch (err) {
      setError("حدث خطأ أثناء جلب روابط الإحالة");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const qActivities = query(
      collection(db, "users_activities"),
      where("userId", "==", currentUser.uid)
    );

    const unsubscribe = onSnapshot(qActivities, async (snapshot) => {
      const activitiesCol = collection(db, "activities");
      const activitiesList = [];

      let primaryRef = null;
      try {
        const refLinksQ = query(
          collection(db, "referralLinks"),
          where("memberId", "==", currentUser.uid),
          limit(1)
        );
        const refLinksSnap = await getDocs(refLinksQ);
        if (!refLinksSnap.empty) primaryRef = refLinksSnap.docs[0].data();
      } catch (e) {
        console.warn("failed to load primary referral link", e);
      }

      for (const docSnap of snapshot.docs) {
        const participation = docSnap.data();
        if (!participation.activityId) continue;

        const activityDoc = await getDoc(
          doc(activitiesCol, participation.activityId)
        );
        if (!activityDoc.exists()) continue;

        const data = activityDoc.data();
        if (data.slug === "share_link") continue;

        activitiesList.push({
          id: activityDoc.id,
          ...data,
          joinedAt: participation.subscribedAt?.toDate() || null,
          status: participation.status,
          shareCount: participation.shareCount || 0,
          projectName: primaryRef?.projectName || null,
          referralLink: primaryRef?.referralLink || null,
          referralCode: primaryRef?.referralCode || null,
        });
      }

      setUserActivities(activitiesList);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const updateProfile = async (updatedData) => {
    try {
      setLoading(true);
      const userDocRef = doc(db, "users", currentUser.uid);
      await updateDoc(userDocRef, updatedData);
      return true;
    } catch (err) {
      setError("حدث خطأ أثناء تحديث البيانات");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    profile,
    referralLinks,
    userActivities,
    loading,
    error,
    updateProfile,
  };
};

export default useProfile;
